import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fetchCustomer, fetchCustomerOrders } from './customerQueries';
import { formatCurrency, formatDate } from '@/lib/utils';

export function CustomerDetailPage() {
  const { customerId = '' } = useParams();

  const { data: customer, isLoading } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => fetchCustomer(customerId),
    enabled: !!customerId,
  });

  const { data: orders = [] } = useQuery({
    queryKey: ['customer', customerId, 'orders'],
    queryFn: () => fetchCustomerOrders(customerId),
    enabled: !!customerId,
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!customer)
    return (
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/crm">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </Button>
        <p className="mt-4 text-sm text-muted-foreground">Customer not found.</p>
      </div>
    );

  return (
    <div>
      <PageHeader
        title={customer.name}
        subtitle="Customer profile and order history"
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/crm">
              <ArrowLeft className="h-4 w-4" /> Back to CRM
            </Link>
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle>Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row icon={<Phone className="h-4 w-4" />} value={customer.phone} />
            {customer.email && (
              <Row icon={<Mail className="h-4 w-4" />} value={customer.email} />
            )}
            {customer.address && (
              <Row icon={<MapPin className="h-4 w-4" />} value={customer.address} />
            )}
            <div className="rounded-md bg-gold-50 p-3 text-center">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Lifetime value
              </div>
              <div className="font-serif text-xl text-gold-700 sm:text-2xl truncate">
                {formatCurrency(customer.totalLifetimeValue ?? 0)}
              </div>
              <div className="text-xs text-muted-foreground">
                from {customer.orderCount ?? 0} order
                {(customer.orderCount ?? 0) === 1 ? '' : 's'}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Order history</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {orders.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No orders yet.
              </div>
            )}
            <div className="divide-y">
              {orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/pos/orders/${o.id}`}
                  className="flex items-center justify-between px-5 py-3 text-sm hover:bg-accent/30"
                >
                  <div>
                    <div className="font-medium">
                      {o.invoiceNumber ?? o.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(
                        // @ts-expect-error firestore timestamp
                        typeof o.createdAt?.toMillis === 'function'
                          ? // @ts-expect-error firestore timestamp
                            o.createdAt.toMillis()
                          : o.createdAt,
                      )}{' '}
                      · {o.items.length} item{o.items.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium tabular-nums">
                      {formatCurrency(o.totalAmount)}
                    </div>
                    <div className="text-xs uppercase text-muted-foreground">
                      {o.paymentMethod}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{icon}</span>
      <span>{value}</span>
    </div>
  );
}
